import clsx from "clsx";
import { uniqueId } from "lodash";
import Quill, { DeltaOperation } from "quill";
import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { removeNonBreakingSpaces } from "../../../helpers/validationHelper";
import { VariableItem, VariablesButton } from "../VariablesButton";
import { IndentStyle } from "./IndentAttributor";
import "./RichEditor.css";
import { Toolbar } from "./Toolbar";
import { variableItems, variableModels, Variables } from "./Variable.type";
import { VariableBlot } from "./VariableBlot";

Quill.register(VariableBlot);

const Size = Quill.import("attributors/style/size");
Size.whitelist = ["10px", "16px", "18px", "32px"];
Quill.register(Size, true);

const AlignStyle = Quill.import("attributors/style/align");
Quill.register(AlignStyle, true);
Quill.register(IndentStyle, true);

const Link = Quill.import("formats/link");
// Override the existing property on the Quill global object and add custom protocols
Link.PROTOCOL_WHITELIST = ["http", "https", "mailto", "tel", "sms"];

const SmsVariables = new Set([
    Variables.ARRIVAL,
    Variables.ARRIVAL_DATE,
    Variables.COMPANY,
    Variables.COMPANY_EMAIL,
    Variables.COMMUNICATION_EMAIL,
    Variables.COMPANY_PHONE,
    Variables.COMPANY_WEBSITE,
    Variables.CUSTOMER_NAME,
    Variables.LISTING_NAME,
    Variables.QUANTITY_COUNT,
    Variables.QUANTITY_COUNT_PPL,
]);
class CustomLinkSanitizer extends Link {
    static sanitize(url: string) {
        // Run default sanitize method from Quill
        const sanitizedUrl = super.sanitize(url);

        // Not whitelisted URL based on protocol so, let's return `blank`
        if (!sanitizedUrl || sanitizedUrl === "about:blank") {
            return sanitizedUrl;
        }

        // Verify if the URL already have a whitelisted protocol
        const hasWhitelistedProtocol = this.PROTOCOL_WHITELIST.some((protocol: string) => {
            return sanitizedUrl.startsWith(protocol);
        });

        if (hasWhitelistedProtocol) {
            return sanitizedUrl;
        }

        // if not, then append only 'http' to not to be a relative URL
        return `https://${sanitizedUrl}`;
    }
}

Quill.register(CustomLinkSanitizer, true);

// Replaces every occurrence of variable like {{purchase.customerName|first_name}} to a variant comprehensible for Quill
// so it be able to render a pretty block
const htmlToEditorText = (html: string) => {
    let text = html;

    for (const model of Object.values(variableModels)) {
        text = text.replaceAll(
            model.pattern,
            ` <span class="ql-variable" data-name="${model.name}" contenteditable="false"></span>`,
        );
    }

    return text;
};

export const htmlToDisabledHtml = (html: string) => {
    let text = html;

    for (const model of Object.values(variableModels)) {
        text = text.replaceAll(model.pattern, `<span>${model.title}</span>`);
    }

    const domParser = new DOMParser().parseFromString(text, "text/html");

    return domParser.querySelector("body")?.innerHTML ?? "";
};

// Backward replacement from Quill format variables to a variant comprehensible for backend
const editorTextToHtml = (content: string) => {
    content = removeNonBreakingSpaces(content);
    const domParser = new DOMParser().parseFromString(content, "text/html");

    // Replace each variable to a text block that comprehensible for back-end
    for (const model of Object.values(variableModels)) {
        const nodes = domParser.querySelectorAll(`span.ql-variable[data-name='${model.name}']`);
        for (const element of nodes) element.replaceWith(model.variableNode(domParser));
    }

    // Clean trash
    for (const element of domParser.querySelectorAll("span.ql-cursor")) {
        element.remove();
    }

    return domParser.querySelector("body")?.innerHTML ?? "";
};

export const hasVariables = (content: string) => {
    const domParser = new DOMParser().parseFromString(content, "text/html");
    const node = domParser.querySelector(`span.ql-variable`);
    return !!node;
};

interface RichEditorProps {
    initialContent: string;
    onChange: (html: string | null) => any;
    showVariableType?: boolean;
    variables?: VariableItem[];
    showVariableButton?: boolean;
    defaultTemplate?: string;
    isSmsEditor?: boolean;
    height?: number;
}

export const RichEditor = ({
    initialContent,
    onChange,
    showVariableType = true,
    variables = variableItems,
    showVariableButton = true,
    defaultTemplate,
    isSmsEditor = false,
    height = 300,
}: RichEditorProps) => {
    const editorRef = useRef<ReactQuill>(null);
    const editor = editorRef.current?.editor;
    const [toolbarId] = useState(uniqueId("RichEditorToolbar"));
    const [value, setValue] = useState(() => htmlToEditorText(initialContent));
    const [editedByUser, setEditedByUser] = useState(false);
    const [marginTop, setMarginTop] = useState<number | null>(null);
    const maxLength = 736;

    const handleOnChange = (value: string, source?: string) => {
        const isUser = editedByUser || source === "user";
        setValue(value);
        if (editorRef.current?.editor?.getText().trim().length === 0 && !hasVariables(value)) {
            onChange("");
            return;
        }

        if (isUser && isSmsEditor) {
            const regex = /(<([^>]+)>)/gi;
            const body = editorTextToHtml(value);
            const replaceBrakeLines = body
                .replace(/<p[^>]*>/g, "")
                .replace(/<\/p>/g, "\n")
                .replace(/&amp;nbsp;|&nbsp;/g, " ")
                .replace("<br>", "\n");
            const result = replaceBrakeLines.replace(regex, "");

            if (editor && editor?.getLength() >= maxLength) {
                editor?.deleteText(maxLength, editor?.getLength());
            }

            onChange(result);
            return;
        }

        if (isUser) {
            onChange(editorTextToHtml(value));
        }
    };

    const handleSelectionChange = (range: any) => {
        if (!range) return;

        const quill = editorRef.current?.getEditor();
        if (!quill) return;
        const bounds = quill.getBounds(range.index);

        setMarginTop(bounds.top + 14);
    };

    useEffect(() => {
        if (marginTop !== null) {
            const tooltip = document.querySelector(".ql-tooltip") as HTMLElement;
            if (tooltip) {
                tooltip.style.setProperty("margin-top", `${marginTop}px`, "important");
            }
        }
    }, [marginTop]);

    useEffect(() => {
        const editor = editorRef.current?.editor;

        // Disable image and rich text copy-paste
        if (editor?.clipboard) {
            editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
                const ops: DeltaOperation[] = [];
                if (delta.ops) {
                    for (const op of delta.ops) {
                        // Allow variables with specific syntax (e.g., {{purchase.customerName|first_name}})
                        if (op.insert && typeof op.insert === "string") {
                            ops.push(op);
                        }

                        // Allow plain text
                        if (op.insert?.variable && typeof op.insert?.variable === "string") {
                            ops.push(op);
                        }
                    }
                }

                delta.ops = ops;
                return delta;
            });
        }

        const handleTextChange = (_: any, __: any, source: string) => {
            // fire onChange event only if template was edited by the user
            if (source === "user") {
                setEditedByUser(true);
                if (editor) {
                    handleOnChange(editor.root.innerHTML, source);
                }
            }

            document.querySelector("#quill-richtext img")?.remove();
        };

        // Add Quill's text-change listener
        if (editor) {
            editor.on("text-change", handleTextChange);
        }

        // Disable image drag and drop
        if (editor) {
            editor.root.addEventListener("drop", handleDrop, false);
        }

        return () => {
            if (editor) {
                editor.root.removeEventListener("drop", handleDrop, false);
                editor.off("text-change", handleTextChange);
            }
        };
    }, []);

    // Reset content when `reset` is triggered
    useEffect(() => {
        if (initialContent === defaultTemplate) {
            setValue(htmlToEditorText(defaultTemplate));
        }
    }, [initialContent, defaultTemplate]);

    useEffect(() => {
        if (initialContent && isSmsEditor) {
            setValue(htmlToEditorText(initialContent?.split("\n").join("<br>")));
            return;
        }

        setValue(htmlToEditorText(initialContent));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className={clsx(
                height ? `h-[${height + 50}px]` : "",
                "mt-1 box-border rounded border-2 border-secondary-lighter",
            )}
        >
            {isSmsEditor ? (
                <div className="box-border rounded border-b-2 border-b-secondary-lighter">
                    <div id={toolbarId}>
                        <VariablesButton
                            items={variableItems.filter((variable) => SmsVariables.has(variable.name as Variables))}
                            showType={showVariableType}
                            onInsertVariable={(event, variableName) => {
                                if (editorRef.current?.editor) {
                                    setEditedByUser(true);
                                    const quill = editorRef.current.editor;
                                    quill.format("variable", variableName);
                                    quill.setSelection(Number(quill.getSelection()?.index ?? 0) + 1, 0, "user");
                                }
                            }}
                        />
                    </div>
                </div>
            ) : (
                <Toolbar id={toolbarId}>
                    {showVariableButton && (
                        <VariablesButton
                            items={variables}
                            showType={showVariableType}
                            onInsertVariable={(event, variableName) => {
                                if (editorRef.current?.editor) {
                                    const quill = editorRef.current.editor;
                                    quill.format("variable", variableName);
                                    quill.setSelection(Number(quill.getSelection()?.index ?? 0) + 1, 0, "user");
                                }
                            }}
                        />
                    )}
                </Toolbar>
            )}

            <ReactQuill
                ref={editorRef}
                id="quill-richtext"
                value={value}
                modules={{ toolbar: { container: `#${toolbarId}` } }}
                style={{ height: `${height}px` }} // (reduced by 50px to accommodate the toolbar space)
                className="overflow-auto"
                onChange={(value) => handleOnChange(value)}
                onChangeSelection={handleSelectionChange}
            />
        </div>
    );
};

const handleDrop = (event: DragEvent) => {
    // Check if the drop includes an image
    if (event.dataTransfer?.types?.includes("Files")) {
        event.preventDefault();
    }
};
